import { Heart, Eye, ShoppingBag, Star } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { optimizeImg } from "../lib/img";

const badgeStyles = {
  yeni: "bg-[#A85C34] text-white",
  indirim: "bg-[#D4A15C] text-white",
  "cok-satan": "bg-[#222222] text-white",
};

const badgeLabels = {
  yeni: "Yeni",
  indirim: "İndirim",
  "cok-satan": "Çok Satan",
};

export default function ProductCard({ product, onAddToCart, onQuickView }) {
  const [favorite, setFavorite] = useState(false);

  const discountPercent = product.oldPrice
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
    : 0;
  const outOfStock = product.stock === 0;

  return (
    <article
      className="group relative bg-white rounded-3xl overflow-hidden border border-[#EAEAEA] hover:border-transparent lift-card"
      data-testid={`product-card-${product.id}`}
    >
      {/* Stretched link: makes the whole card navigable while letting the
          action buttons above (higher z-index) intercept their own clicks. */}
      <Link
        to={`/urun/${product.id}`}
        className="absolute inset-0 z-[1]"
        aria-label={product.name}
        data-testid={`product-link-${product.id}`}
      />

      {/* Image — pointer-events-none lets clicks fall through to the
          stretched Link behind it; interactive children opt back in. */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F8F8F8] zoom-on-hover pointer-events-none">
        <img
          src={optimizeImg(product.image, 500)}
          alt={product.name}
          width="500"
          height="625"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-[2] pointer-events-none">
          {outOfStock && (
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#888888] text-white">
              Tükendi
            </span>
          )}
          {product.badges?.map((b) => (
            <span
              key={b}
              className={`text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full ${badgeStyles[b]}`}
              data-testid={`product-badge-${b}`}
            >
              {b === "indirim" && discountPercent > 0 ? `%${discountPercent}` : badgeLabels[b]}
            </span>
          ))}
        </div>

        {/* Favorite */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setFavorite((f) => !f);
          }}
          aria-label="Favorilere ekle"
          data-testid={`product-favorite-${product.id}`}
          className="pointer-events-auto z-[2] absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 backdrop-blur-md flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Heart
            className={`w-4 h-4 transition-colors ${
              favorite ? "fill-[#D4A15C] text-[#D4A15C]" : "text-[#222222]"
            }`}
          />
        </button>

        {/* Hover actions */}
        <div className="absolute bottom-3 left-3 right-3 z-[2] flex gap-2 opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-300 pointer-events-none">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickView?.(product);
            }}
            data-testid={`product-quick-view-${product.id}`}
            className="pointer-events-auto flex-1 inline-flex items-center justify-center gap-2 h-11 bg-white/95 backdrop-blur-md text-[#222222] rounded-full text-sm font-medium hover:bg-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Hızlı İncele
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-[2] p-5 pointer-events-none">
        {product.rating != null && (
          <div className="flex items-center gap-1 mb-2" data-testid={`product-rating-${product.id}`}>
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.round(product.rating) ? "fill-[#D4A15C] text-[#D4A15C]" : "text-[#EAEAEA]"
                }`}
              />
            ))}
            <span className="text-xs text-[#888888] ml-1">{product.rating.toFixed(1)}</span>
          </div>
        )}

        <h3 className="font-serif text-lg leading-snug text-[#222222] mb-1 line-clamp-1" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        <p className="text-sm text-[#888888] line-clamp-2 mb-4 leading-relaxed">
          {product.desc}
        </p>

        <div className="flex items-end justify-between gap-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-serif text-2xl text-[#A85C34] font-semibold" data-testid={`product-price-${product.id}`}>
              {product.price.toLocaleString("tr-TR")}₺
            </span>
            {product.oldPrice && (
              <span className="text-sm text-[#888888] line-through">
                {product.oldPrice.toLocaleString("tr-TR")}₺
              </span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!outOfStock) onAddToCart?.(product);
            }}
            disabled={outOfStock}
            aria-label={outOfStock ? "Tükendi" : "Sepete ekle"}
            data-testid={`product-add-to-cart-${product.id}`}
            className="pointer-events-auto w-11 h-11 rounded-full bg-[#A85C34] hover:bg-[#8B4726] text-white flex items-center justify-center transition-all active:scale-95 shadow-[0_4px_14px_rgba(168,92,52,0.3)] disabled:bg-[#CCCCCC] disabled:shadow-none disabled:cursor-not-allowed disabled:hover:bg-[#CCCCCC]"
          >
            <ShoppingBag className="w-4 h-4" />
          </button>
        </div>
      </div>
    </article>
  );
}
